import {
  Button,
  FlexBox,
  Page,
  SearchInput,
  SimplePagination,
  Table,
  Text,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import upperFirst from 'lodash/upperFirst';
import { useNavigate } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';

import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import {
  AutomationCreatePayloadT,
  AutomationListingT,
  withAutomations,
  WithAutomationsInjectedPropsT,
} from 'hocs';
import { AutomationChannelHumanNames } from 'hocs/automations/api/types';
import { useNotifications } from 'hooks/useNotifications';

import { AutomationDotsMenu } from './automationDotsMenu';
import { EditAutomationModal } from './editAutomationModal';
import { ToggleAutomation } from './toggleAutomation';

export type AutomationsOwnProps = {};

export type AutomationsDispatchProps = {};

export type AutomationsStateProps = {};

type AutomationsPropsT = WithAutomationsInjectedPropsT &
  AutomationsOwnProps &
  AutomationsDispatchProps &
  AutomationsStateProps;

type AutomationsStateT = {
  waitingForFetch: boolean;
  isConfirmModalOpen: boolean;
  confirmAutomation: null | AutomationListingT;
  isCreateModalOpen: boolean;
  editModalID: null | string;
};

const COLUMN_COUNT = 5;

export const AutomationsImpl = (props: AutomationsPropsT): JSX.Element => {
  const navigate = useNavigate();
  const notify = useNotifications();

  const [state, setState] = useSetState<AutomationsStateT>({
    waitingForFetch: false,
    isConfirmModalOpen: false,
    confirmAutomation: null,
    isCreateModalOpen: false,
    editModalID: null,
  });

  useMount(() => {
    props.automations.getItems();
  });

  const handleSearchSubmit = (searchTerm: string) => {
    const { automations } = props;
    if (searchTerm !== automations.searchTerm) {
      ampli.automationsSearchApplied();
      automations.searchTermChange(searchTerm, 0);
    }
  };

  const handleSearchToggle = (isOpen: boolean) => {
    if (isOpen) {
      ampli.automationsSearchOpened();
    } else {
      const { automations } = props;
      ampli.automationsSearchClosed();
      if (automations.searchTerm !== '') {
        automations.searchTermChange('', 0);
      }
    }
  };

  const gotoNextPage = () => {
    ampli.automationsPaginationNextClicked();
    props.automations.nextPage();
  };

  const gotoPreviousPage = () => {
    ampli.automationsPaginationPreviousClicked();
    props.automations.previousPage();
  };

  const handleRefresh = () => {
    props.automations.getItems();
  };

  const showCreateModal = () => {
    setState({ isCreateModalOpen: true, editModalID: null });
  };

  const showEditModal = (id: string) => {
    setState({ isCreateModalOpen: true, editModalID: id });
  };

  const handleCreateModalClose = () => {
    setState({ isCreateModalOpen: false });
  };

  const handleGotoAutomationFlow = (automationId: string) => {
    navigate(`/settings/automations/flow/${automationId}`);
  };

  const handleGotoAuditLogs = (automationId: string) => {
    navigate(`/settings/automations/flow/${automationId}/history`);
  };

  const handleDuplicateAutomation = (automationId: string) => {
    const { automations } = props;
    automations
      .getItem(automationId)
      .then((getResponse) => {
        const { id, created_at, updated_at, ...automation } = getResponse.data;
        const automationCopyName = `${automation.name} (copy)`;
        const automationCopy: AutomationCreatePayloadT = {
          ...automation,
          name: automationCopyName,
          active: false,
        };

        return automations.create(automationCopy).then((createResponse) => {
          notify({
            title: `Automation copied`,
            text: `Automation was succesfully copied as "${automationCopyName}"`,
            className: 'success',
            time: 5000,
            icon: 'done',
          });
          handleGotoAutomationFlow(createResponse.data.id);
        });
      })
      .catch(() => {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to copy automation`,
          icon: 'exclamation',
          className: 'danger',
        });
      });
  };

  const handleOpenConfirmDeleteAutomation = (automationId: string) => {
    const { automations } = props;
    const deleteAutomation = automations.items.find(
      (automation) => automation.id === automationId,
    );
    if (deleteAutomation) {
      setState({
        isConfirmModalOpen: true,
        confirmAutomation: deleteAutomation,
      });
    }
  };

  const handleDeleteAutomation = () => {
    const { automations } = props;
    const { confirmAutomation } = state;
    if (confirmAutomation) {
      automations
        .remove(confirmAutomation.id)
        .then(() => {
          handleRefresh();
          closeConfirmDeleteAutomation();
          notify({
            title: `Automation deleted`,
            text: `Automation was succesfully deleted`,
            className: 'success',
            time: 5000,
            icon: 'done',
          });
        })
        .catch(() => {
          closeConfirmDeleteAutomation();
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to delete automation`,
            icon: 'exclamation',
            className: 'danger',
          });
        });
    }
  };

  const closeConfirmDeleteAutomation = () => {
    setState({
      isConfirmModalOpen: false,
      confirmAutomation: null,
    });
  };

  const renderFoot = () => {
    const { automations } = props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = automations;
    const hasFoot = hasNextPage || hasPreviousPage;

    if (!hasFoot) {
      return null;
    }

    return (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT}>
            <SimplePagination
              previousButtonClick={gotoPreviousPage}
              nextButtonClick={gotoNextPage}
              previousButtonDisabled={!hasPreviousPage}
              nextButtonDisabled={!hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );
  };

  const renderLoadingStatus = () => {
    const { automations } = props;

    if (automations.getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td colSpan={COLUMN_COUNT}>
            Something went wrong...
            <Button variant="tertiary" onClick={handleRefresh}>
              Try again
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }

    return <TableLoadingRow colSpan={COLUMN_COUNT} />;
  };

  const renderRow = (automation: AutomationListingT) => {
    return (
      <Table.Tr key={automation.id}>
        <Table.Td>
          <FlexBox flexDirection="column" gap="100">
            <Text.Paragraph variant="2">{automation.name}</Text.Paragraph>

            <Text.Paragraph variant="4" subdued>
              {automation.description}
            </Text.Paragraph>
          </FlexBox>
        </Table.Td>

        <Table.Td>
          {upperFirst(
            automation.channels
              .map((name) => AutomationChannelHumanNames[name])
              .join(', '),
          )}
        </Table.Td>

        <Table.Td>
          {automation.updated_at ? (
            <Timestamp relative time={automation.updated_at} />
          ) : (
            '-'
          )}
        </Table.Td>

        <Table.Td>
          <ToggleAutomation
            automation={automation}
            automations={props.automations}
            disabled={props.automations.updateAsyncStatus !== 'succeeded'}
          />
        </Table.Td>

        <Table.Td>
          <AutomationDotsMenu
            automationId={automation.id}
            showSettingsModal={showEditModal}
            gotoAutomationFlow={handleGotoAutomationFlow}
            gotoAuditLogs={handleGotoAuditLogs}
            duplicateAutomation={handleDuplicateAutomation}
            deleteAutomation={handleOpenConfirmDeleteAutomation}
          />
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderBody = () => {
    const { waitingForFetch } = state;
    const { automations } = props;
    const { items, getItemsAsyncStatus } = automations;

    if (waitingForFetch || getItemsAsyncStatus !== 'succeeded') {
      return renderLoadingStatus();
    }

    if (items.length > 0) {
      return items.map(renderRow);
    }

    return (
      <Table.Tr>
        <Table.Td colSpan={COLUMN_COUNT}>No automations found.</Table.Td>
      </Table.Tr>
    );
  };

  const { automations } = props;
  const {
    editModalID,
    isCreateModalOpen,
    isConfirmModalOpen,
    confirmAutomation,
  } = state;

  return (
    <>
      <Page.Section
        title="All automations"
        controls={
          <>
            <SearchInput
              onSubmit={handleSearchSubmit}
              onToggle={handleSearchToggle}
            />

            <Button onClick={showCreateModal} id="add-automation-btn">
              Add automation
            </Button>
          </>
        }
      >
        <Table framed stickyHeader stickyFooter headerWithBackground>
          <Table.Thead>
            <Table.Tr>
              <Table.Th width="50%">Name</Table.Th>
              <Table.Th width="25%">Channel</Table.Th>
              <Table.Th width="25%">Updated</Table.Th>
              <Table.Th>Enable</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>{renderBody()}</Table.Tbody>

          {renderFoot()}
        </Table>
      </Page.Section>

      <EditAutomationModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        automationId={editModalID}
        automations={automations}
        gotoFlow={handleGotoAutomationFlow}
        refreshPage={handleRefresh}
      />

      <ConfirmModal
        isShown={isConfirmModalOpen}
        headline="Are you sure?"
        text={`Are you sure you want to delete the automation
        "${confirmAutomation?.name}"?`}
        confirmText="Yes, delete it now"
        cancelText="Cancel"
        confirm={handleDeleteAutomation}
        cancel={closeConfirmDeleteAutomation}
      />
    </>
  );
};

export const Automations = withAutomations(AutomationsImpl);
